import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PagedResponse } from '../../../shared/contract/paged-response.contract';
import { EquipmentTypeFilterView } from '../../../shared/filters/contract/filter-view/equipment-type-filter-view.contract';
import { getHttpParameters } from '../../../shared/utils/get-http-parameters';
import { AcknowledgeAlarmCommand } from '../contract/acknowledge-alarm-command.contract';
import { AlarmFilterCollection } from '../contract/alarm-filter-collection-view.contract';
import { AlarmFilterRequestParams } from '../contract/alarm-filter-request-params.contract';
import { AlarmRequestParams } from '../contract/alarm-request.contract';
import { AlarmTypeConfig } from '../contract/alarm-type-config.contract';
import { Alarm } from '../contract/alarm.contract';
import { CreateAlarmRuleCommand } from '../contract/create-alarm-rule-command.contract';
import { CreateAlarmTypeConfigCommand } from '../contract/create-alarm-type-settings-command.contract';
import { toCreateTransportAlarmRuleCommand } from '../contract/create-transport-alarm-rule-command.contract';
import { CustomerWithSharesRequest } from '../contract/customer-with-shares-request.contract';
import { Customer } from '../contract/customer.contract';
import { DeleteAlarmRuleCommand } from '../contract/delete-alarm-rule-command.contract';
import { EquipmentStatus } from '../contract/equipment-status.contract';
import { Equipment } from '../contract/equipment.contract';
import { EquipmentsRequest } from '../contract/equipments-request.contract';
import { SearchAlarmRequestParams } from '../contract/search-alarm-request.contract';
import { UpdateAlarmRuleCommand } from '../contract/update-alarm-rule-command.contract';
import { UpdateAlarmTypeConfigCommand } from '../contract/update-alarm-type-settings.contract';
import { UpdateDefaultConfigsCommand } from '../contract/update-default-recipients-command.contract';
import { toUpdateEquipmentAlarmRuleCommand } from '../contract/update-equipment-alarm-rule-command.contract';
import { toUpdateTransportAlarmRuleCommand } from '../contract/update-transport-alarm-rule-command.contract';
import { ViewAlarmRule } from '../contract/view-alarm-rule.contract';

@Injectable({ providedIn: 'root' })
export class NotificationApiService {
  private baseUrl = environment.APP_NOTIFICATION_SERVICE_BASE_URL + '/api/v1/notifications/alarms';
  private equipmentUrl = environment.APP_NOTIFICATION_SERVICE_BASE_URL + '/api/v1/notifications/equipmentalarms';
  private transportUrl = environment.APP_NOTIFICATION_SERVICE_BASE_URL + '/api/v1/notifications/transportalarms';
  private configUrl = environment.APP_NOTIFICATION_SERVICE_BASE_URL + '/api/v1/notifications/alarm-type-configs';

  constructor(private http: HttpClient) {}

  public getAlarmRules(request: AlarmRequestParams): Observable<PagedResponse<ViewAlarmRule>> {
    const httpParams = getHttpParameters(request);
    return this.http.get<PagedResponse<ViewAlarmRule>>(`${this.baseUrl}/rules`, { params: httpParams });
  }

  public searchAlarmRules(request: SearchAlarmRequestParams): Observable<PagedResponse<ViewAlarmRule>> {
    return this.http.post<PagedResponse<ViewAlarmRule>>(`${this.baseUrl}/rules/search`, JSON.stringify(request));
  }

  public filterAlarmRules(request: AlarmFilterRequestParams): Observable<AlarmFilterCollection> {
    const httpParams = getHttpParameters(request);
    return this.http.get<AlarmFilterCollection>(`${this.baseUrl}/rules/filter`, { params: httpParams });
  }

  public getCurrentActive(): Observable<PagedResponse<Alarm>> {
    return this.http.get<PagedResponse<Alarm>>(`${this.baseUrl}/current-active`);
  }

  public acknowledgeAlarm(command: AcknowledgeAlarmCommand): Observable<string> {
    return this.http.put(`${this.baseUrl}/${command.id}/acknowledge`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  // Equipment Alarm Methods
  public createEquipmentAlarmRule(command: CreateAlarmRuleCommand): Observable<string> {
    return this.http.post(`${this.equipmentUrl}/rules/create`, JSON.stringify(command), { responseType: 'text' });
  }

  public updateEquipmentAlarmRule(command: UpdateAlarmRuleCommand): Observable<string> {
    const equipmentCommand = toUpdateEquipmentAlarmRuleCommand(command);
    return this.http.post(`${this.equipmentUrl}/rules/update`, JSON.stringify(equipmentCommand), {
      responseType: 'text',
    });
  }

  public removeEquipmentAlarmRule({ alarmRuleId }: DeleteAlarmRuleCommand): Observable<string> {
    return this.http.post(`${this.equipmentUrl}/rules/delete`, JSON.stringify({ alarmRuleId }), {
      responseType: 'text',
    });
  }

  public getDistinctEquipmentTypes(): Observable<EquipmentTypeFilterView[]> {
    return this.http.get<EquipmentTypeFilterView[]>(`${this.equipmentUrl}/grouped-equipment-types`);
  }

  public getDistinctLabels(): Observable<string[]> {
    return this.http.get<string[]>(`${this.equipmentUrl}/distinct-labels`);
  }

  public getCustomersWithShares(request: CustomerWithSharesRequest): Observable<PagedResponse<Customer>> {
    const httpParams = getHttpParameters(request);
    return this.http.get<PagedResponse<Customer>>(`${this.equipmentUrl}/customer-with-shares`, { params: httpParams });
  }

  public getEquipmentStatuses(): Observable<EquipmentStatus[]> {
    return this.http.get<EquipmentStatus[]>(`${this.equipmentUrl}/active-equipment-statuses`);
  }

  public getEquipments(request: EquipmentsRequest): Observable<PagedResponse<Equipment>> {
    const httpParams = getHttpParameters(request);
    return this.http.get<PagedResponse<Equipment>>(`${this.equipmentUrl}/equipments`, { params: httpParams });
  }

  // Transport Alarm Methods
  public createTransportAlarmRule(command: CreateAlarmRuleCommand): Observable<string> {
    const transportCommand = toCreateTransportAlarmRuleCommand(command);
    return this.http.post(`${this.transportUrl}/rules/create`, JSON.stringify(transportCommand), {
      responseType: 'text',
    });
  }

  public updateTransportAlarmRule(command: UpdateAlarmRuleCommand): Observable<string> {
    const transportCommand = toUpdateTransportAlarmRuleCommand(command);
    return this.http.post(`${this.transportUrl}/rules/update`, JSON.stringify(transportCommand), {
      responseType: 'text',
    });
  }

  public removeTransportAlarmRule({ alarmRuleId }: DeleteAlarmRuleCommand): Observable<string> {
    return this.http.post(`${this.transportUrl}/rules/delete`, JSON.stringify({ alarmRuleId }), {
      responseType: 'text',
    });
  }

  public isEquipmentOwner(): Observable<boolean> {
    return this.http.get<boolean>(`${this.equipmentUrl}/is-equipment-owner`);
  }

  // Alarm Settings Methods
  public createAlarmTypeConfig(command: CreateAlarmTypeConfigCommand): Observable<string> {
    return this.http.post(`${this.configUrl}`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public updateAlarmTypeConfig(command: UpdateAlarmTypeConfigCommand): Observable<string> {
    return this.http.put(`${this.configUrl}/${command.alarmTypeConfigId}`, JSON.stringify(command), {
      responseType: 'text',
    });
  }

  public removeAlarmTypeSettings(configId: string) {
    return this.http.delete(`${this.configUrl}/${configId}`);
  }

  public getAlarmTypeSettings(): Observable<AlarmTypeConfig[]> {
    return this.http.get<AlarmTypeConfig[]>(`${this.configUrl}`);
  }

  public updateDefaultEmailRecipients(command: UpdateDefaultConfigsCommand): Observable<ViewAlarmRule> {
    return this.http.put<ViewAlarmRule>(`${this.baseUrl}/rules/default-configs`, JSON.stringify(command));
  }
}
